import { environmentHttpClient as httpClient } from "client/Api/httpClient";
import {
	HubCommentModel,
	HubCommentPagingInformationModel,
	HubCommentSaveModel,
	HubCommentStatisticsModel,
	ReactionCounterModel,
	ReactionKind,
	ReactionUserModel
} from "client/Api/Hub/common";
import { SortDirection } from "../../common";

export function getCommentsList(
	eventId: number,
	amount: number | null,
	afterComment: HubCommentPagingInformationModel | null,
	sortDirection: SortDirection,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<HubCommentModel>> {
	return httpClient.get<LazyLoadPageResult<HubCommentModel>>(
		`/UiApi/Events/${eventId}/Comments`,
		{
			amount,
			afterComment,
			sortDirection
		},
		{ signal }
	);
}

export function getRelatedCommentThread(
	eventId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<readonly HubCommentModel[]> {
	return httpClient.get<readonly HubCommentModel[]>(
		`/UiApi/Events/${eventId}/Comments/${commentId}/Thread`,
		undefined,
		{ signal }
	);
}

export function registerCommentReaction(
	eventId: number,
	commentId: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<HubCommentStatisticsModel> {
	return httpClient.post<HubCommentStatisticsModel>(
		`/UiApi/Events/${eventId}/Comments/${commentId}/Reactions`,
		kind,
		{ signal }
	);
}

export function updateCommentReaction(
	eventId: number,
	commentId: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<HubCommentStatisticsModel> {
	return httpClient.put<HubCommentStatisticsModel>(
		`/UiApi/Events/${eventId}/Comments/${commentId}/Reactions`,
		kind,
		{ signal }
	);
}

export function unregisterCommentReaction(
	eventId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<HubCommentStatisticsModel> {
	return httpClient.delete<HubCommentStatisticsModel>(
		`/UiApi/Events/${eventId}/Comments/${commentId}/Reactions`,
		undefined,
		{ signal }
	);
}

export function getCommentReactionCounts(
	eventId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<readonly ReactionCounterModel[]> {
	return httpClient.get<readonly ReactionCounterModel[]>(
		`/UiApi/Events/${eventId}/Comments/${commentId}/Reactions/Counts`,
		undefined,
		{ signal }
	);
}

export function getCommentReactions(
	eventId: number,
	commentId: number,
	pageSize: number,
	skip: number,
	kind: ReactionKind | undefined,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<ReactionUserModel>> {
	return httpClient.get<LazyLoadPageResult<ReactionUserModel>>(
		`/UiApi/Events/${eventId}/Comments/${commentId}/Reactions`,
		{
			kind,
			pageSize,
			skip
		},
		{ signal }
	);
}

export function createComment(
	eventId: number,
	newComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.post<HubCommentModel>(
		`/UiApi/Events/${eventId}/Comments/`,
		newComment,
		{ signal }
	);
}

export function editComment(
	eventId: number,
	commentId: number,
	updatedComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.put<HubCommentModel>(
		`/UiApi/Events/${eventId}/Comments/${commentId}`,
		updatedComment,
		{ signal }
	);
}

export function deleteComment(
	eventId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.delete<void>(
		`/UiApi/Events/${eventId}/Comments/${commentId}`,
		undefined,
		{ signal }
	);
}

export function createReply(
	eventId: number,
	parentCommentId: number,
	newComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.post<HubCommentModel>(
		`/UiApi/Events/${eventId}/Comments/${parentCommentId}/Replies`,
		newComment,
		{ signal }
	);
}
